import { API } from '@whaleden/core/api';
import commonRarity from '@whaleden/core/assets/img/rarities/common.svg';
import squareRarity from '@whaleden/core/assets/img/rarities/epic.svg';
import rareRarity from '@whaleden/core/assets/img/rarities/rare.svg';
import polygonRarity from '@whaleden/core/assets/img/rarities/ultimate.svg';
import { ImgProxySize, type ImgProxyConstraintOptions } from '@whaleden/core/types/Document';
import { FilterMatchMode } from 'primevue/api';
import { MintRarity } from '~/types/ComicBook';

export const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

export const buildFilterString = (filters: any) => {
    const filterStrings = Object.keys(filters)
        .map((key) => {
            const filter = filters[key];

            if (filter.value !== null && filter.value !== '') {
                if (Array.isArray(filter.value)) {
                    const values = filter.value.map((item: any) => item.id || item);

                    return `${key}=in=(${values.join(',')})`;
                } else {
                    switch (filter.matchMode) {
                        case FilterMatchMode.CONTAINS:
                            return `${key}=ilike='${filter.value}'`;
                        case FilterMatchMode.STARTS_WITH:
                            return `${key}=ilike='${filter.value}*'`;
                        case FilterMatchMode.ENDS_WITH:
                            return `${key}=ilike='*${filter.value}'`;

                        default:
                            return `${key}=ilike='*${filter.value}*'`;
                    }
                }
            }
            return '';
        })
        .filter(Boolean)
        .join(';');

    return filterStrings.length > 0 ? filterStrings : undefined;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getHeadlinerFullName = (authors: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const headliner = authors?.find((o: any) => o.headliner);
    return headliner ? headliner.author.fullName : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNonHeadlinerFullNames = <T>(authors: T[]) => {
    return (
        authors
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ?.filter((o: any) => !o.headliner)
    );
};

export const getImg = (imgId: string, size = ImgProxySize.SIZE_W_1200) => {
    return API.DocumentService.getFileById(imgId, {
        size: size,
    });
};

export const bgRarityColor = (rarity: MintRarity) => {
    switch (rarity) {
        case MintRarity.COMMON:
            return 'bg-green-600/10';

        case MintRarity.RARE:
            return 'bg-primary/10';

        case MintRarity.EPIC:
            return 'bg-violet-500/10';

        case MintRarity.ULTIMATE:
            return 'bg-amber-500/10';
    }
};

export const rarityColor = (rarity: MintRarity) => {
    switch (rarity) {
        case MintRarity.COMMON:
            return 'text-green-600';

        case MintRarity.RARE:
            return 'text-primary';

        case MintRarity.EPIC:
            return 'text-violet-500';

        case MintRarity.ULTIMATE:
            return 'text-amber-500';
    }
};

export const rarityImg = (rarity: MintRarity) => {
    switch (rarity) {
        case MintRarity.COMMON:
            return commonRarity;

        case MintRarity.RARE:
            return rareRarity;

        case MintRarity.EPIC:
            return squareRarity;

        case MintRarity.ULTIMATE:
            return polygonRarity;
    }
};

export function buildImgProxyQueryString(options: ImgProxyConstraintOptions): string {
    const params = new URLSearchParams();

    if (options.watermark !== undefined) params.append('watermark', String(options.watermark));
    if (options.quality75 !== undefined) params.append('quality75', String(options.quality75));
    if (options.size) params.append('size', options.size);
    if (options.opacity !== undefined) params.append('opacity', String(options.opacity));
    if (options.position) params.append('position', options.position);
    if (options.x_offset !== undefined) params.append('x_offset', String(options.x_offset));
    if (options.y_offset !== undefined) params.append('y_offset', String(options.y_offset));
    if (options.scale !== undefined) params.append('scale', String(options.scale));

    return params.toString();
}

const mintToTokenMapping: Record<string, string> = {
    So11111111111111111111111111111111111111112: 'wSOL',
    EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: 'USDC',
    Gh9ZwEmdLJ8DscKNTkTqPbNwLNNBjuSzaG9Vp2KGtKJr: 'USDC',
};

export function getTokenName(mintAddress?: string): string | null {
    return mintAddress ? mintToTokenMapping[mintAddress] || null : null;
}
